import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiArrowRightSLine, RiPlugLine } from '@remixicon/react';
import { CONNECTOR_TYPES, type ConnectorDto, type ConnectorType } from '@atnt/shared';
import { ApiError } from '@/shared/api/client';
import { cn } from '@/shared/lib/cn';
import { Badge, Button, SidePanel, toast } from '@/shared/ui';
import { confirmDiscard, panelEscape } from '@/features/devices/components/NamePanel';
import { useHotkey } from '@/features/settings/hotkeys';
import type { ConnectorsContext } from '../backend';
import { useSaveConnector } from '../api';
import type { ConnectorRights } from '../permissions';
import { buildInput, ConnectionTest, ConnectorFields, draftOf, newDraft, type ConnectorDraft } from './ConnectorForm';
import { TYPE_META, TypeChip } from './parts';

/**
 * "Konnektor qo'shish" / "Konnektorni tahrirlash" — docked 400 px side panel (the device registry pattern, 586:15488):
 * step 1 picks the source type (cards: icon, name, one-line description), step 2 is the type-specific form with
 * "Ulanishni tekshirish"; footer "Bekor qilish" | "Saqlash". Edit mode opens straight on the form.
 */
export function ConnectorPanel({
  ctx,
  rights,
  connector,
  initialType,
  siteCode,
  unifiedExists,
  onClose,
  onSaved,
}: {
  ctx: ConnectorsContext;
  rights: ConnectorRights;
  /** undefined = create */
  connector: ConnectorDto | undefined;
  initialType?: ConnectorType;
  /** this tenant's unified-server site code (real `externalSiteId`) */
  siteCode: string | null;
  /** an ATNT unified-server connector already serves this reservoir (only one is allowed) */
  unifiedExists: boolean;
  onClose: () => void;
  onSaved: (connector: ConnectorDto, created: boolean) => void;
}) {
  const { t } = useTranslation('connectors');
  const formId = useId();
  const save = useSaveConnector(ctx);
  const mode = connector ? 'edit' : 'create';
  const startType = initialType && !(initialType === 'atnt_unified' && unifiedExists) ? initialType : undefined;
  const [initial, setInitial] = useState<ConnectorDraft | null>(() =>
    connector ? { ...draftOf(connector), siteCode: siteCode ?? '' } : startType ? newDraft(startType, siteCode) : null,
  );
  const [draft, setDraft] = useState<ConnectorDraft | null>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dirty = draft != null && JSON.stringify(draft) !== JSON.stringify(initial);
  const editable = connector ? rights.canEdit(connector) : rights.create;

  const pickType = (type: ConnectorType) => {
    const next = draft?.type === type ? draft : newDraft(type, siteCode);
    setInitial(next);
    setDraft(next);
    setErrors({});
  };

  const close = async () => {
    if (save.isPending) return;
    if (dirty && !(await confirmDiscard(t))) return;
    onClose();
  };

  const submit = async () => {
    if (!draft || !editable) return;
    const built = buildInput(draft, mode, t);
    if (built.errors) {
      setErrors(built.errors);
      return;
    }
    setErrors({});
    try {
      const saved = await save.mutateAsync({ id: connector?.id, input: built.input });
      toast.success(connector ? t('panel.saved') : t('panel.created'), { subtitle: saved.name });
      onSaved(saved, !connector);
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fieldErrors).length) {
        setErrors(Object.fromEntries(Object.entries(err.fieldErrors).map(([k, v]) => [k.replace(/^config\./, ''), v])));
      } else toast.error(t('error'), { subtitle: err instanceof Error ? err.message : undefined });
    }
  };

  useHotkey('save', () => void submit(), { enabled: Boolean(draft) });

  const type = draft?.type;
  const title = connector ? t('panel.editTitle') : t('panel.createTitle');
  const meta = type ? TYPE_META[type] : null;

  return (
    <SidePanel
      width={400}
      onBack={() => void close()}
      backLabel={t('panel.back')}
      icon={{ icon: meta?.icon ?? RiPlugLine, color: meta?.color ?? 'pink' }}
      title={title}
      aria-label={title}
      stepper={
        connector
          ? undefined
          : {
              steps: [t('panel.stepType'), t('panel.stepSettings')],
              current: draft ? 1 : 0,
              onStepClick: (i) => {
                if (i === 0) setDraft(null);
              },
            }
      }
      footer={
        draft ? (
          <>
            <Button variant="secondary" className="flex-none!" onClick={() => void close()} disabled={save.isPending}>
              {t('cancel')}
            </Button>
            <Button variant="primary-alt" type="submit" form={formId} loading={save.isPending} disabled={!editable}>
              {t('save')}
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={() => void close()}>
            {t('cancel')}
          </Button>
        )
      }
    >
      {!draft ? (
        <TypePicker selected={initial?.type} unifiedExists={unifiedExists} onPick={pickType} />
      ) : (
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Esc inside the docked panel's form closes the panel (open popups inside handle their own Esc)
        <form
          id={formId}
          noValidate
          className="flex flex-col gap-14"
          onKeyDown={panelEscape(() => void close())}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {!connector && (
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="flex cursor-pointer items-center gap-10 rounded-8 bg-field p-8 text-left transition-colors hover:bg-hover"
              aria-label={t('panel.changeType')}
            >
              <TypeChip type={draft.type} size={28} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-13 font-semibold text-fg">{t(`types.${draft.type}.name`)}</span>
                <span className="truncate text-11 text-fg-secondary">{t('panel.changeType')}</span>
              </span>
              <RiArrowRightSLine aria-hidden className="size-16 text-icon" />
            </button>
          )}
          <ConnectorFields
            draft={draft}
            onChange={(patch) => {
              setDraft((d) => (d ? { ...d, ...patch } : d));
              const keys = Object.keys(patch);
              if (keys.some((k) => errors[k] || k === 'registers')) setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !keys.some((p) => k === p || k.startsWith(`${p}.`)))));
            }}
            errors={errors}
            mode={mode}
            canChooseOrg={rights.orgAdmin}
            readOnly={!editable}
            compact
          />
          <ConnectionTest
            ctx={ctx}
            connectorId={connector?.id ?? null}
            disabled={!rights.operate && !rights.create}
            getConfig={() => {
              const built = buildInput(draft, mode, t, true);
              if (built.errors) {
                setErrors(built.errors);
                return null;
              }
              return built.config;
            }}
          />
        </form>
      )}
    </SidePanel>
  );
}

/** Step 1: one card per source type. */
function TypePicker({ selected, unifiedExists, onPick }: { selected?: ConnectorType; unifiedExists: boolean; onPick: (type: ConnectorType) => void }) {
  const { t } = useTranslation('connectors');
  return (
    <div className="flex flex-col gap-8">
      <p className="text-xs text-fg-secondary">{t('panel.pickHint')}</p>
      <ul className="flex flex-col gap-8" aria-label={t('panel.stepType')}>
      {CONNECTOR_TYPES.map((type) => {
        const blocked = type === 'atnt_unified' && unifiedExists;
        return (
          <li key={type}>
            <button
              type="button"
              disabled={blocked}
              onClick={() => onPick(type)}
              className={cn(
                'card-accent flex w-full cursor-pointer items-center gap-12 p-12 text-left transition-colors duration-150 hover:bg-hover',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
                selected === type && 'bg-selected',
              )}
            >
              <TypeChip type={type} size={36} />
              <span className="flex min-w-0 flex-1 flex-col gap-2">
                <span className="flex min-w-0 items-center gap-6">
                  <span className="truncate text-sm font-semibold text-fg">{t(`types.${type}.name`)}</span>
                  {blocked ? <Badge color="green">{t('panel.connected')}</Badge> : TYPE_META[type].soon ? <Badge color="neutral">{t('panel.soon')}</Badge> : null}
                </span>
                <span className="line-clamp-2 text-xs leading-17 tracking-normal text-fg-secondary">{t(`types.${type}.desc`)}</span>
              </span>
              <RiArrowRightSLine aria-hidden className="size-16 shrink-0 text-icon" />
            </button>
          </li>
        );
      })}
      </ul>
    </div>
  );
}
